/**
 * 错误上报：聚合、落盘、入队（对齐移动端 errorHandler.js）
 * 刻意不依赖 store / router / axios，避免与 request 循环引用。
 */

import errorConfig from './errorConfig.js'
import { getDeviceReportInfo } from './deviceReportInfo.js'

function normalizeError (input) {
  if (input instanceof Error) return input
  if (input && typeof input === 'object' && input.message) {
    const e = new Error(String(input.message))
    if (input.stack) e.stack = input.stack
    return e
  }
  return new Error(typeof input === 'string' ? input : JSON.stringify(input))
}

function getUserSnapshot () {
  try {
    const raw = localStorage.getItem('userInfo')
    const userInfo = raw ? JSON.parse(raw) : null
    if (!userInfo || typeof userInfo !== 'object') {
      return { id: '', nickname: '' }
    }
    return {
      id: String(userInfo.id || userInfo.userId || ''),
      nickname: String(userInfo.nickname || userInfo.nickName || userInfo.name || ''),
    }
  } catch (e) {
    return { id: '', nickname: '' }
  }
}

function getPageSnapshot () {
  try {
    if (typeof window === 'undefined' || !window.location) return {}
    const { pathname, search, hash } = window.location
    const options = {}
    try {
      const params = new URLSearchParams(search || '')
      params.forEach((v, k) => { options[k] = v })
    } catch (e) { /* ignore */ }
    return {
      route: `${pathname || ''}${search || ''}${hash || ''}` || '/',
      options,
      depth: 1,
    }
  } catch (e) {
    return {}
  }
}

function getSystemInfo () {
  try {
    return getDeviceReportInfo()
  } catch (e) {
    return {}
  }
}

class ErrorHandler {
  constructor () {
    this.isInitialized = false
    this.batchTimer = null
  }

  async init () {
    if (this.isInitialized) return
    try {
      await errorConfig.getConfig()
      await this.reportStoredErrors()
      this.startBatchTimer()
      this.isInitialized = true
    } catch (e) {
      console.warn('[error-report] init failed', e)
    }
  }

  startBatchTimer () {
    if (this.batchTimer) clearInterval(this.batchTimer)
    this.batchTimer = setInterval(() => {
      errorConfig.reportBatch()
    }, errorConfig.config.batchInterval)
  }

  async reportStoredErrors () {
    if (!errorConfig.config.enableStorage) return
    try {
      const list = errorConfig.readStoredList()
      if (!list.length) return
      for (const item of list) {
        await errorConfig.addToQueue(item)
      }
      await errorConfig.reportBatch()
    } catch (e) { /* ignore */ }
  }

  collectErrorInfo (error, errorType, additionalInfo = {}) {
    const err = normalizeError(error)
    return {
      id: `err_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      type: errorType,
      error: {
        message: err.message || '未知错误',
        stack: err.stack || '',
        name: err.name || 'Error',
        code: err.code,
        statusCode: err.statusCode,
      },
      user: getUserSnapshot(),
      page: getPageSnapshot(),
      system: getSystemInfo(),
      environment: { env: 'PC' },
      additional: additionalInfo,
    }
  }

  async handleJSError (error, source, lineno, colno) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(error)
    const msg = e.message || ''
    // 与 main.js 一致：忽略浏览器 ResizeObserver 噪音
    if (msg.includes('ResizeObserver')) return
    const info = this.collectErrorInfo(e, 'javascript', { source, lineno, colno })
    await this.reportError(info)
  }

  async handlePromiseError (reason) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(reason)
    if ((e.message || '').includes('ResizeObserver')) return
    const info = this.collectErrorInfo(e, 'promise', {})
    await this.reportError(info)
  }

  async handleVueError (error, vm, info) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(error)
    const name = vm?.$options?.name || vm?.$options?.__name || vm?.type?.name || 'Unknown'
    const vueInfo = this.collectErrorInfo(e, 'vue', {
      componentName: name,
      lifecycleInfo: info || '',
    })
    await this.reportError(vueInfo)
  }

  async handleApiError (url, error, requestInfo = {}) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(error)
    const info = this.collectErrorInfo(e, 'api', {
      url,
      ...requestInfo,
    })
    await this.reportError(info)
  }

  async handleBusinessError (businessType, error, customData = {}) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(error)
    const info = this.collectErrorInfo(e, 'business', {
      businessType,
      ...customData,
    })
    await this.reportError(info)
  }

  async handleNetworkError (error, requestInfo = {}) {
    if (!(await errorConfig.isErrorReportEnabled())) return
    const e = normalizeError(error)
    const info = this.collectErrorInfo(e, 'network', requestInfo)
    await this.reportError(info)
  }

  async reportError (errorInfo) {
    try {
      const key = errorConfig.generateErrorKey(errorInfo)
      if (errorConfig.hasReported(key)) return
      errorConfig.markAsReported(key)
      if (errorConfig.config.enableConsole) {
        console.warn('[error-report]', errorInfo.type, errorInfo.error?.message)
      }
      await errorConfig.addToQueue(errorInfo)
      if (errorConfig.config.enableStorage) {
        this.saveToLocal(errorInfo)
      }
    } catch (e) {
      console.warn('[error-report] reportError', e)
    }
  }

  saveToLocal (errorInfo) {
    try {
      const list = errorConfig.readStoredList()
      list.push(errorInfo)
      while (list.length > 100) list.shift()
      errorConfig.writeStoredList(list)
    } catch (e) { /* ignore */ }
  }

  async manualReportError (errorType, message, additionalInfo = {}) {
    const err = new Error(message)
    const info = this.collectErrorInfo(err, errorType, additionalInfo)
    await this.reportError(info)
  }

  getErrorStats () {
    return {
      reportedCount: errorConfig.reportedErrors.size,
      pendingCount: errorConfig.pendingReports.length,
      isReporting: errorConfig.isReporting,
      storedCount: errorConfig.getStoredErrorCount(),
    }
  }

  clearErrorRecords () {
    errorConfig.reportedErrors.clear()
    errorConfig.pendingReports = []
    errorConfig.removeStored()
  }

  destroy () {
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
      this.batchTimer = null
    }
    this.isInitialized = false
  }
}

export default new ErrorHandler()
